import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Printer, Receipt, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COMPANY_INFO = {
  name: "NJK Logistics Pte Ltd",
  address: "212A Punggol Walk #12-741 Singapore 821212",
  phone: "98366186",
  email: "inquiry@njklogistics.com"
};

export const PaymentReceiptDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReceipt();
  }, [id]);

  const fetchReceipt = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payment-receipts/${id}`);
      setReceipt(response.data);
    } catch (error) {
      toast.error('Failed to load payment receipt');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-SG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Receipt Not Found</h2>
          <Button onClick={() => navigate('/admin/billing')}>Back to Billing</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-40">
      <div className="bg-white border-b sticky top-0 z-10 print:hidden">
        <div className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/admin/billing')}>
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{receipt.document_number}</h1>
                <p className="text-gray-600 mt-1">Payment Receipt Details</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handlePrint} variant="outline" data-testid="print-receipt-btn">
                <Printer size={20} className="mr-2" />
                Print
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 print:px-0 print:py-0">
        <div className="max-w-3xl mx-auto">
          <Card className="print:shadow-none print:border-none">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-8 border-b pb-6">
                <div>
                  <h2 className="text-2xl font-bold text-purple-600">{COMPANY_INFO.name}</h2>
                  <p className="text-gray-600 mt-1">{COMPANY_INFO.address}</p>
                  <p className="text-gray-600">Tel: {COMPANY_INFO.phone}</p>
                  <p className="text-gray-600">Email: {COMPANY_INFO.email}</p>
                </div>
                <div className="text-right">
                  <h1 className="text-3xl font-bold text-gray-900">PAYMENT RECEIPT</h1>
                  <p className="text-xl font-semibold text-purple-600 mt-2">{receipt.document_number}</p>
                </div>
              </div>

              {/* Success Banner */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8 flex items-center gap-4 print:bg-white">
                <div className="bg-green-100 p-3 rounded-full">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-green-800">Payment Received</h3>
                  <p className="text-green-700">Thank you for your payment</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">Received From:</h3>
                  <p className="font-bold text-lg">{receipt.customer_name}</p>
                  <p className="text-gray-600">{receipt.customer_email}</p>
                  <p className="text-gray-600">{receipt.customer_phone}</p>
                </div>
                <div className="text-right">
                  <div className="mb-2">
                    <span className="text-gray-600">Receipt Date: </span>
                    <span className="font-semibold">{formatDate(receipt.created_at)}</span>
                  </div>
                  <div className="mb-2">
                    <span className="text-gray-600">Payment Date: </span>
                    <span className="font-semibold">{receipt.payment_date}</span>
                  </div>
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-gray-50 p-6 rounded-lg mb-8 print:bg-white print:border print:border-gray-200">
                <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                  <Receipt size={20} className="text-purple-600" />
                  Payment Details
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Invoice Number:</span>
                    <span className="font-semibold">{receipt.invoice_number}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Payment Method:</span>
                    <span className="font-semibold">{receipt.payment_method}</span>
                  </div>
                  {receipt.reference_number && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600">Reference Number:</span>
                      <span className="font-semibold">{receipt.reference_number}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-3 text-xl">
                    <span className="font-bold">Amount Received:</span>
                    <span className="font-bold text-green-600">${receipt.payment_amount.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {receipt.notes && (
                <div className="border-t pt-6">
                  <h3 className="font-semibold text-gray-700 mb-2">Notes:</h3>
                  <p className="text-gray-600">{receipt.notes}</p>
                </div>
              )}

              <div className="border-t pt-6 mt-6 text-center text-gray-600">
                <p className="text-sm">This is a computer-generated receipt. No signature required.</p>
                <p className="text-sm mt-2">Thank you for your business!</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
